import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  type PropsWithChildren,
} from 'react';
import axios, { AxiosError } from 'axios';
import { supabase } from './supabase';
import { queryClient } from '@/lib/query-client';
import {
  authControllerCreateSession,
  authControllerDeleteSession,
  authControllerGetSession,
} from '@/generated/api/endpoints/auth/auth';
import type {
  AvailableTenantDto,
  SessionResponseDto,
  SessionResponseDtoFrontendUserState,
  SessionTenantDto,
} from '@/generated/api/models';
import { initialAuthState, type AuthState } from './auth-state';
import { authReducer, initialMachineState, MAX_PROFILE_ATTEMPTS } from './auth-machine';

export interface LoginResult {
  ok: boolean;
  error?: string;
  /** The established session on success — carries the routing decision. */
  session?: SessionResponseDto;
}

/**
 * Resolved once authentication settles, or after a bounded wait — timing out
 * yields `frontendUserState: null` (server-error) so guards never hang.
 */
export interface EnsuredProfile {
  frontendUserState: SessionResponseDtoFrontendUserState | null;
  hasUser: boolean;
  profile: SessionResponseDto | null;
  availableTenants: AvailableTenantDto[];
  currentTenant: SessionTenantDto | null;
}

interface AuthContextValue extends AuthState {
  /** Sign in to Supabase, then exchange the token for a fleetster session cookie. */
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  /** Re-fetch the current session (e.g. after a tenant change on another subdomain). */
  refresh: () => Promise<void>;
  /** Await a settled profile — used by route `beforeLoad` guards. */
  ensureProfile: () => Promise<EnsuredProfile>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const sleep = (ms: number): Promise<void> => new Promise(r => setTimeout(r, ms));
const ENSURE_TIMEOUT_MS = 5000;

export function AuthProvider(props: PropsWithChildren) {
  const [state, dispatch] = useReducer(authReducer, initialMachineState);

  // Mirror the latest machine state into a ref so `ensureProfile` can read it
  // without being re-created on every transition.
  const stateRef = useRef(state);
  useLayoutEffect(() => {
    stateRef.current = state;
  });
  // Resolvers waiting for a terminal phase (authenticated | unauthenticated).
  const waitersRef = useRef<(() => void)[]>([]);

  const isTerminal = (phase: string): boolean => phase === 'authenticated' || phase === 'unauthenticated';

  useEffect(() => {
    if (isTerminal(state.phase)) {
      const waiters = waitersRef.current;
      waitersRef.current = [];
      waiters.forEach(resolve => resolve());
    }
  }, [state.phase]);

  // Bootstrap the session once on load, before the router renders (App shows a
  // splash until the phase leaves 'initializing').
  const refresh = useCallback(async () => {
    try {
      const res = await authControllerGetSession();
      dispatch({ type: 'PROFILE_LOADED', session: res.data });
    } catch {
      // 401 (no/expired session) or any bootstrap error → treat as logged out.
      dispatch({ type: 'SIGN_OUT_DONE' });
    }
  }, []);

  useEffect(() => {
    // Only the initial bootstrap; subsequent refetches go through refresh().
    void (async () => {
      try {
        const res = await authControllerGetSession();
        dispatch({ type: 'BOOTSTRAP_FOUND', session: res.data });
      } catch {
        dispatch({ type: 'BOOTSTRAP_EMPTY' });
      }
    })();
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    dispatch({ type: 'SIGN_IN_START' });
    const result = await supabase.auth.signInWithPassword({ email, password });
    if (result.error) {
      dispatch({ type: 'SIGN_IN_FAIL', error: result.error.message });
      return { ok: false, error: result.error.message };
    }
    const token = result.data.session?.access_token;
    if (!token) {
      const error = 'Supabase returned no access token';
      dispatch({ type: 'SIGN_IN_FAIL', error });
      return { ok: false, error };
    }

    // Phase 2: exchange the provider token for a fleetster session, with a
    // bounded backoff retry on transient failures.
    dispatch({ type: 'PROVIDER_AUTHED' });
    for (let attempt = 0; attempt < MAX_PROFILE_ATTEMPTS; attempt++) {
      try {
        const res = await authControllerCreateSession({ token });
        queryClient.clear();
        dispatch({ type: 'PROFILE_LOADED', session: res.data });
        return { ok: true, session: res.data };
      } catch (error) {
        const httpStatus = (error as AxiosError).response?.status;
        if (httpStatus === 403) {
          // Authenticated by Supabase but not a fleetster user — terminal.
          await supabase.auth.signOut().catch(() => {});
          dispatch({ type: 'SIGN_IN_FAIL', error: 'no-access' });
          return { ok: false, error: 'This account has no access to fleetster.' };
        }
        if (attempt < MAX_PROFILE_ATTEMPTS - 1) {
          await sleep(250 * 2 ** attempt);
          dispatch({ type: 'PROFILE_RETRY' });
          continue;
        }
        await supabase.auth.signOut().catch(() => {});
        dispatch({ type: 'SIGN_IN_FAIL', error: 'session-failed' });
        return { ok: false, error: 'Could not establish a session. Please try again.' };
      }
    }
    return { ok: false, error: 'Could not establish a session. Please try again.' };
  }, []);

  const logout = useCallback(async () => {
    dispatch({ type: 'SIGN_OUT_START' });
    // Best-effort teardown on both sides; local state is cleared regardless.
    try {
      await authControllerDeleteSession();
    } catch {
      /* already gone */
    }
    try {
      await supabase.auth.signOut();
    } catch {
      /* already gone */
    }
    queryClient.clear();
    dispatch({ type: 'SIGN_OUT_DONE' });
  }, []);

  // Tear down local session state without a fleetster round-trip — the server has
  // already told us the session is gone (via a 401). No DELETE call, so this can
  // never re-trigger the interceptor below into a loop.
  const expireSession = useCallback(() => {
    queryClient.clear();
    void supabase.auth.signOut().catch(() => {});
    dispatch({ type: 'SIGN_OUT_DONE' });
  }, []);

  // A 401 while authenticated means the session expired server-side; the
  // `router.invalidate()` bridge in App.tsx re-runs the guards on the drop to
  // `unauthenticated`. The phase check skips the bootstrap's expected 401s.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      response => response,
      (error: unknown) => {
        const status = (error as AxiosError).response?.status;
        if (status === 401 && stateRef.current.phase === 'authenticated') {
          expireSession();
        }
        return Promise.reject(error);
      },
    );
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [expireSession]);

  const ensureProfile = useCallback(async (): Promise<EnsuredProfile> => {
    if (!isTerminal(stateRef.current.phase)) {
      await new Promise<void>(resolve => {
        const timer = setTimeout(resolve, ENSURE_TIMEOUT_MS);
        waitersRef.current.push(() => {
          clearTimeout(timer);
          resolve();
        });
      });
    }
    const session = stateRef.current.session;
    return {
      frontendUserState: session?.frontendUserState ?? null,
      hasUser: session?.user != null,
      profile: session,
      availableTenants: session?.availableTenants ?? [],
      currentTenant: session?.currentTenant ?? null,
    };
  }, []);

  const session = state.session;
  const value: AuthContextValue = {
    phase: state.phase,
    isAuthenticated: state.phase === 'authenticated',
    frontendUserState: session?.frontendUserState ?? null,
    user: session?.user ?? null,
    currentTenant: session?.currentTenant ?? null,
    availableTenants: session?.availableTenants ?? [],
    login,
    logout,
    refresh,
    ensureProfile,
  };

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { initialAuthState };
export type { AuthState };
